import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarClock } from "lucide-react";
import { PHASE_TEMPLATE, DEFERRAL_REASONS } from "@/lib/basProcess/phases";

interface Props {
  projectId: string;
  /** T.ex. "risksammanstallning" eller "arbetsomradesutredning". */
  sourceType: string;
  sourceId: string;
  questionId: string;
  questionText?: string | null;
  currentPhase?: string | null;
  onDone?: () => void;
  trigger?: React.ReactNode;
}

/** Skjut fram en fråga till ett senare skede – med motivering och spårbarhet. */
export const DeferQuestionDialog: React.FC<Props> = ({
  projectId,
  sourceType,
  sourceId,
  questionId,
  questionText,
  currentPhase,
  onDone,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [toPhase, setToPhase] = useState<string>("");
  const [reasonCode, setReasonCode] = useState<string>("");
  const [reasonText, setReasonText] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!toPhase) return toast.error("Välj vilket skede frågan flyttas till");
    if (!reasonCode) return toast.error("Välj en orsak");
    if (reasonCode === "annat" && !reasonText.trim())
      return toast.error("Beskriv orsaken");

    setBusy(true);
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    const displayName =
      (user?.user_metadata?.full_name as string) || user?.email || "Okänd användare";

    const { error } = await supabase.from("question_deferrals").insert({
      project_id: projectId,
      source_type: sourceType,
      source_id: sourceId,
      question_id: questionId,
      question_text: questionText ?? null,
      from_phase: currentPhase ?? null,
      to_phase: toPhase,
      reason_code: reasonCode,
      reason_text: reasonText.trim() || null,
      decided_by: user?.id ?? null,
      decided_by_name: displayName,
    });
    setBusy(false);

    if (error) {
      toast.error("Kunde inte flytta fram frågan");
      return;
    }
    toast.success("Frågan är framflyttad och loggad");
    setOpen(false);
    setReasonText("");
    setReasonCode("");
    setToPhase("");
    onDone?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <CalendarClock className="mr-1 h-4 w-4" /> Flytta fram
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Flytta fram frågan till ett senare skede</DialogTitle>
          <DialogDescription>
            Frågan räknas då som hanterad i nuvarande skede, men står kvar som öppen punkt
            i det skede du väljer. Beslutet loggas med ditt namn och din motivering.
          </DialogDescription>
        </DialogHeader>

        {questionText && (
          <p className="rounded-md bg-muted p-3 text-sm">{questionText}</p>
        )}

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Flyttas till skede</Label>
            <Select value={toPhase} onValueChange={setToPhase}>
              <SelectTrigger>
                <SelectValue placeholder="Välj skede" />
              </SelectTrigger>
              <SelectContent>
                {PHASE_TEMPLATE.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Orsak</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger>
                <SelectValue placeholder="Välj orsak" />
              </SelectTrigger>
              <SelectContent>
                {DEFERRAL_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Motivering</Label>
            <Textarea
              rows={3}
              placeholder="Varför kan frågan inte besvaras nu?"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Avbryt
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? "Sparar..." : "Flytta fram frågan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeferQuestionDialog;
